import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useLiveRefresh } from "@/lib/live";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Trash2, Pencil, Search } from "lucide-react";
import { toast } from "sonner";

const blank = () => ({ code: "", substrate_type: "Paper", description: "", gsm: "", manufacturer: "", unit: "MTRS", notes: "", active: true });

/**
 * Substrates master. Each row defines Code, Substrate Type, Description, GSM
 * and Manufacturer so the MJC Substrate combobox can auto-fill the spec.
 * Rendered inside Inventory.jsx as a tab.
 */
export default function SubstratesPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);

  const load = async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const { data } = await api.get("/substrates", { params: { q, limit: 1000 } });
      setRows(data);
    } catch (e) { if (!quiet) toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useLiveRefresh("masters", () => load({ quiet: true }));

  const del = async (id) => {
    if (!window.confirm("Delete this substrate?")) return;
    try { await api.delete(`/substrates/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="border border-neutral-200 bg-white" data-testid="substrates-panel">
      <div className="px-4 py-3 border-b border-neutral-200 flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input data-testid="substrates-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search code / description / manufacturer" className="pl-8 h-9 rounded-sm" />
        </div>
        <Button variant="outline" onClick={load} className="rounded-sm h-9">Apply</Button>
        <div className="flex-1" />
        <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
          <DialogTrigger asChild>
            <Button data-testid="substrate-add-btn" onClick={() => setEditing(blank())} className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00] gap-1.5"><Plus size={13} /> Add Substrate</Button>
          </DialogTrigger>
          {editing && <SubstrateEditor row={editing} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />}
        </Dialog>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="substrates-table">
          <thead className="bg-neutral-50 label-caps text-[10px]">
            <tr>
              <th className="text-left px-3 py-2.5">Code</th>
              <th className="text-left px-3 py-2.5">Type</th>
              <th className="text-left px-3 py-2.5">Description</th>
              <th className="text-right px-3 py-2.5">GSM</th>
              <th className="text-left px-3 py-2.5">Manufacturer</th>
              <th className="text-left px-3 py-2.5">Unit</th>
              <th className="text-left px-3 py-2.5">Status</th>
              <th className="w-24"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="text-center py-8 text-neutral-400">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-neutral-400" data-testid="substrates-empty">No substrates yet — click Add Substrate to create one.</td></tr>}
            {!loading && rows.map((r) => (
              <tr key={r.id} className="border-t border-neutral-100 data-row" data-testid={`substrate-row-${r.id}`}>
                <td className="px-3 py-2 font-mono-num font-medium">{r.code}</td>
                <td className="px-3 py-2 text-xs">{r.substrate_type}</td>
                <td className="px-3 py-2">{r.description || "—"}</td>
                <td className="px-3 py-2 text-right font-mono-num">{r.gsm ?? "—"}</td>
                <td className="px-3 py-2">{r.manufacturer || "—"}</td>
                <td className="px-3 py-2 text-xs">{r.unit}</td>
                <td className="px-3 py-2">
                  <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${r.active ? "border-emerald-200 text-emerald-700 bg-emerald-50" : "border-neutral-200 text-neutral-600 bg-neutral-50"}`}>
                    {r.active ? "Active" : "Archived"}
                  </span>
                </td>
                <td className="px-3 py-2">
                
                  <div className="flex items-center gap-1 justify-end">
                    <Button variant="ghost" size="icon" data-testid={`substrate-edit-${r.id}`} onClick={() => setEditing(r)} className="h-7 w-7"><Pencil size={13} /></Button>
                    <Button variant="ghost" size="icon" data-testid={`substrate-del-${r.id}`} onClick={() => del(r.id)} className="h-7 w-7 text-rose-600"><Trash2 size={13} /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SubstrateEditor({ row, onSaved, onCancel }) {
  const [f, setF] = useState({ ...row });
  const [saving, setSaving] = useState(false);
  const isEdit = !!row.id;

  const save = async () => {
    if (!f.code || !f.substrate_type) { toast.error("Code and Substrate Type are required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...f,
        gsm: f.gsm === "" || f.gsm == null ? null : Number(f.gsm),
      };
      if (isEdit) await api.patch(`/substrates/${row.id}`, payload);
      else await api.post("/substrates", payload);
      toast.success(isEdit ? "Substrate updated" : "Substrate added");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <DialogContent className="max-w-xl">
      <DialogHeader>
        <DialogTitle>{isEdit ? "Edit" : "New"} Substrate</DialogTitle>
        <DialogDescription>Adding here makes the code selectable in Master Job Card and hydrates its spec fields.</DialogDescription>
      </DialogHeader>
      <div className="grid grid-cols-2 gap-3">
        <F label="Code *" testid="sub-code" value={f.code} onChange={(v) => setF({ ...f, code: v })} />
        <div>
          <label className="label-caps block mb-1 text-[10px]">Substrate Type *</label>
          <Select value={f.substrate_type} onValueChange={(v) => setF({ ...f, substrate_type: v })}>
            <SelectTrigger data-testid="sub-type" className="rounded-sm h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["Paper", "PP", "BOPP", "PE", "PET", "PVC", "Tyvek", "Thermal Transfer", "Direct Thermal", "Foil", "Vinyl", "Other"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <F label="Description" testid="sub-desc" value={f.description} onChange={(v) => setF({ ...f, description: v })} full />
        <F label="GSM" testid="sub-gsm" type="number" value={f.gsm ?? ""} onChange={(v) => setF({ ...f, gsm: v })} />
        <F label="Manufacturer" testid="sub-mfg" value={f.manufacturer} onChange={(v) => setF({ ...f, manufacturer: v })} />
        <F label="Unit" testid="sub-unit" value={f.unit} onChange={(v) => setF({ ...f, unit: v })} />
        <F label="Notes" testid="sub-notes" value={f.notes} onChange={(v) => setF({ ...f, notes: v })} full />
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel} data-testid="sub-cancel">Cancel</Button>
        <Button disabled={saving} onClick={save} data-testid="sub-save" className="bg-[#FF5A00] hover:bg-[#e04f00]">{saving ? "Saving…" : "Save"}</Button>
      </DialogFooter>
    </DialogContent>
  );
}

function F({ label, value, onChange, testid, type = "text", full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="label-caps block mb-1 text-[10px]">{label}</label>
      <Input data-testid={testid} type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="rounded-sm h-9 text-sm" />
    </div>
  );
}
